import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import {
  buildItineraryConfigDoc,
  fetchItineraryConfig,
  fetchItineraryConfigFresh,
  itineraryItemsFromDoc,
  replaceItineraryItem,
  saveItineraryConfig,
  type ItineraryConfigDoc,
} from "./khushiAccess";
import { KHUSHI_ITINERARY, type KhushiItineraryItem } from "./khushiData";
import { KHUSHI_COLORS, KHUSHI_FONTS } from "./theme";

const { gold, goldSoft, ink } = KHUSHI_COLORS;
const { display: fontDisplay, mono: fontMono } = KHUSHI_FONTS;

function safeStringify(v: unknown): string {
  return JSON.stringify(v, null, 2);
}

function normalizeDoc(input: any): ItineraryConfigDoc {
  const orderIn: unknown = input?.order;
  const itemsByIdIn: unknown = input?.itemsById;
  const itemsById: Record<string, KhushiItineraryItem> =
    itemsByIdIn && typeof itemsByIdIn === "object"
      ? { ...(itemsByIdIn as any) }
      : {};

  const orderRaw = Array.isArray(orderIn) ? (orderIn as any[]) : [];
  const order: string[] = [];
  const seen = new Set<string>();
  for (const id of orderRaw) {
    if (typeof id !== "string") continue;
    if (seen.has(id)) continue;
    if (!itemsById[id]) continue;
    order.push(id);
    seen.add(id);
  }

  for (const [id, item] of Object.entries(itemsById)) {
    if (!item || typeof item !== "object") continue;
    if ((item as any).id !== id) (item as any).id = id;
    if (!seen.has(id)) order.push(id);
  }

  return { order, itemsById };
}

function parseJson(input: string): any {
  return JSON.parse(input);
}

export default function KhushiFullItineraryAdmin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [docValue, setDocValue] = useState<ItineraryConfigDoc | null>(null);
  const [fullJson, setFullJson] = useState("");

  const [itemId, setItemId] = useState<string>("");
  const [itemJson, setItemJson] = useState<string>("");
  const [itemError, setItemError] = useState<string | null>(null);
  const [itemSaved, setItemSaved] = useState(false);

  const items = useMemo(() => {
    if (docValue) return itineraryItemsFromDoc(docValue);
    return KHUSHI_ITINERARY;
  }, [docValue]);

  const refresh = async (fresh: boolean) => {
    setSaved(false);
    setItemSaved(false);
    setError(null);
    setLoading(true);
    try {
      const loaded = fresh
        ? await fetchItineraryConfigFresh()
        : await fetchItineraryConfig();
      const base = loaded ?? buildItineraryConfigDoc(KHUSHI_ITINERARY);
      setDocValue(base);
      setFullJson(safeStringify(base));
      const firstId = base.order[0] ?? "";
      setItemId((prev) => prev || firstId);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load itinerary config.");
      setDocValue(null);
      setFullJson("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!itemId) return;
    const item =
      docValue?.itemsById?.[itemId] ??
      items.find((i) => i.id === itemId) ??
      null;
    if (item) setItemJson(safeStringify(item));
  }, [itemId, docValue, items]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullJson);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 900);
    } catch {
      setError("Copy failed. Your browser may block clipboard access.");
    }
  };

  const saveAll = async () => {
    setSaved(false);
    setError(null);
    setLoading(true);
    try {
      const parsed = parseJson(fullJson);
      const normalized = normalizeDoc(parsed);
      await saveItineraryConfig(normalized);
      setDocValue(normalized);
      setFullJson(safeStringify(normalized));
      setSaved(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save itinerary config.");
    } finally {
      setLoading(false);
    }
  };

  const replaceOne = async () => {
    setItemSaved(false);
    setItemError(null);
    setLoading(true);
    try {
      const parsed = parseJson(itemJson);
      const item = parsed as KhushiItineraryItem;
      if (!item?.id || typeof item.id !== "string") {
        throw new Error("Item JSON must include a string `id`.");
      }
      await replaceItineraryItem(item);
      await refresh(true);
      setItemSaved(true);
    } catch (e: unknown) {
      setItemError(e instanceof Error ? e.message : "Failed to replace item.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: fontDisplay,
            fontSize: "0.8rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: gold,
            mb: { xs: 0.5, sm: 0 },
          }}
        >
          Full itinerary config
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => refresh(false)}
            disabled={loading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: "rgba(212, 175, 55, 0.5)",
              color: goldSoft,
              "&:hover": { borderColor: gold },
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => refresh(true)}
            disabled={loading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: "rgba(212, 175, 55, 0.5)",
              color: goldSoft,
              "&:hover": { borderColor: gold },
            }}
          >
            Refresh (fresh)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => copy()}
            disabled={loading || !fullJson}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: "rgba(212, 175, 55, 0.5)",
              color: goldSoft,
              "&:hover": { borderColor: gold },
            }}
          >
            Copy JSON
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => saveAll()}
            disabled={loading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              bgcolor: gold,
              color: "#2c0a12",
              "&:hover": { bgcolor: goldSoft },
            }}
          >
            Save all
          </Button>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: fontMono,
          fontSize: "0.75rem",
          color: goldSoft,
          opacity: 0.9,
          mb: 2,
        }}
      >
        Edit {"{ order, itemsById }"}. Each item may include {"revealAt"} (ISO
        string) to control when it unlocks.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saved && !error && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved.
        </Alert>
      )}
      {loading && !docValue ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} sx={{ color: gold }} />
        </Box>
      ) : (
        <TextField
          multiline
          minRows={12}
          value={fullJson}
          onChange={(e) => setFullJson(e.target.value)}
          placeholder="Paste the full itinerary doc JSON here."
          fullWidth
          sx={{
            mb: 2.5,
            "& textarea": { color: ink, fontFamily: fontMono, fontSize: "0.82rem" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(212, 175, 55, 0.35)",
            },
          }}
        />
      )}

      <Divider sx={{ borderColor: "rgba(212, 175, 55, 0.25)", my: 2 }} />

      <Typography
        sx={{
          fontFamily: fontDisplay,
          fontSize: "0.75rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: gold,
          mb: 1.5,
        }}
      >
        Replace one item
      </Typography>

      {itemError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {itemError}
        </Alert>
      )}
      {itemSaved && !itemError && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Item replaced.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
        <TextField
          select
          size="small"
          label="Item id"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          sx={{
            minWidth: 260,
            "& label": { color: goldSoft, fontFamily: fontDisplay },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(212, 175, 55, 0.35)",
            },
            "& .MuiSelect-select": { color: ink, fontFamily: fontMono },
          }}
          InputLabelProps={{ shrink: true }}
        >
          {items.map((it) => (
            <MenuItem key={it.id} value={it.id} sx={{ fontFamily: fontMono }}>
              {it.id}
            </MenuItem>
          ))}
        </TextField>

        <Button
          size="small"
          variant="contained"
          onClick={() => replaceOne()}
          disabled={loading}
          sx={{
            fontFamily: fontDisplay,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            bgcolor: gold,
            color: "#2c0a12",
            "&:hover": { bgcolor: goldSoft },
          }}
        >
          Replace item
        </Button>
      </Box>

      <TextField
        multiline
        minRows={8}
        value={itemJson}
        onChange={(e) => setItemJson(e.target.value)}
        placeholder='Paste a single item JSON like {"id":"leave","timeLabel":"...","title":"..."}'
        fullWidth
        sx={{
          "& textarea": { color: ink, fontFamily: fontMono, fontSize: "0.82rem" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(212, 175, 55, 0.35)",
          },
        }}
      />
    </Box>
  );
}

